/**
 * SSE-aware polling for unread notification count. Real-time updates via SSE
 * when connected; falls back to 10s polling when SSE is down.
 *
 * Two hooks:
 *  - `useNotificationCount` — owns the polling lifecycle. Mount once in BadgeProvider.
 *  - `useNotificationCountValue` — read-only subscriber for components that just
 *    need the current value. Avoids spinning up a duplicate polling timer
 *    (each QueryObserver maintains its own `refetchInterval`).
 */

import { useQuery, skipToken } from '@tanstack/react-query';
import { getNotificationUnreadCount } from '@api-client/api-client';
import { notificationCountKey } from '@lib/cache/notifications';
import { useAuth } from './useAuth';
import { useEventStreamState } from './useEventStreamState';
import { AuthSessionState } from '@providers/auth/authTypes';

interface NotificationCountValue {
  unread_count: number;
}

/**
 * Hook for polling unread notification count. Mount once globally in BadgeProvider.
 *
 * @param enabled - Whether to enable polling (default: true)
 * @param pollingInterval - Polling interval in milliseconds (default: 10000 = 10 seconds)
 * @returns React Query result with unread count data
 */
export function useNotificationCount(enabled = true, pollingInterval = 10000) {
  const { user, sessionState } = useAuth();
  const { isConnected } = useEventStreamState();
  // Issue #567: Stop polling during recovery so the invalid_grant cascade
  // doesn't get amplified by 10-second tokenless requests.
  const isSessionBlocked =
    sessionState === AuthSessionState.SESSION_EXPIRED ||
    sessionState === AuthSessionState.REAUTHENTICATING;
  const shouldPoll = enabled && !isSessionBlocked;

  return useQuery<NotificationCountValue>({
    queryKey: notificationCountKey(),
    queryFn: async () => {
      // N3 "honest bell": the server counts unread rows directly (no page scan,
      // no client-side status filter), so the badge can't diverge from the true
      // unread set - a suppressed/hidden row never inflates it.
      const response = await getNotificationUnreadCount();
      return { unread_count: response.data.count };
    },
    enabled: !!user && shouldPoll,

    // AIDEV-NOTE: Two orthogonal gates — SSE absorbs real-time updates when connected,
    // and Issue #567 pauses polling during auth recovery. Both must be satisfied to poll.
    refetchInterval: isConnected ? false : shouldPoll ? pollingInterval : false,
    refetchIntervalInBackground: false, // Foreground reconnect + full-sync handles catch-up
    refetchOnMount: 'always', // CRITICAL: Fetch immediately on mount for instant badge
    refetchOnWindowFocus: true, // Refetch when user returns to tab

    staleTime: pollingInterval * 0.8, // Consider data fresh for 80% of interval
    gcTime: pollingInterval * 2, // Keep in cache for 2x interval
  });
}

/**
 * Read-only subscriber for the unread notification count cache.
 *
 * Returns `data` reactively from the same cache key as `useNotificationCount`.
 * Does NOT spin up its own polling timer — relies on `useNotificationCount`
 * (mounted in BadgeProvider) to drive fetches. Use this in components that
 * consume the count but don't own the polling lifecycle (Header).
 */
export function useNotificationCountValue() {
  return useQuery<NotificationCountValue>({
    queryKey: notificationCountKey(),
    queryFn: skipToken,
  });
}
